import { Directive, ElementRef, Renderer2, Input, AfterViewInit, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { RestProviderService } from '../../services/rest-provider/rest-provider.service';
import { TranslationsProviderService } from '../../services/translations-provider/translations-provider.service';
import { UserService } from '../../services/user/user-provider.service';

/**
 * semSpellcheck — advisory Slovene spell-check overlay for a native <input>/<textarea>.
 *
 * ADVISORY ONLY: never blocks a save. When enabled it draws a red wavy squiggle under each
 * misspelled word (debounced / on blur, NOT per keystroke). Clicking a squiggled word opens a
 * popover with frequency-ranked suggestions + an "Add to dictionary" action.
 *
 * Technique: a transparent-text "mirror" backdrop is layered behind the field (which is given a
 * transparent background). The mirror repeats the field text with the misspelled ranges wrapped in
 * a <span> that carries only a red wavy text-decoration — so the squiggle shows through under the
 * real (on-top) field text. Font metrics / padding / scroll are copied from the field so the
 * squiggle tracks the text exactly.
 *
 * Field context (ClassType + Field) is sent to api/language/spellcheck so the per-field whitelist
 * suppresses real catalogue proper-nouns (essential on name/title fields).
 */
@Directive({
    selector: '[semSpellcheck]',
    standalone: false
})
export class SpellcheckDirective implements AfterViewInit, OnDestroy {

    @Input('semSpellcheck') enabled: boolean | string;
    @Input() scLanguage: string = 'sl';
    @Input() scClassType: string = '';
    @Input() scField: string = '';

    private host: HTMLInputElement | HTMLTextAreaElement;
    private isTextarea = false;
    private container: HTMLElement;
    private backdrop: HTMLElement;
    private highlights: HTMLElement;
    private popover: HTMLElement | null = null;
    private misspellings: Array<{ word: string, offset: number, length: number, suggestions: string[] }> = [];
    private debounceTimer: any = null;
    private teardown: Array<() => void> = [];
    private measureCtx: CanvasRenderingContext2D | null = null;
    private setupDone = false;

    private static styleInjected = false;

    /**
     * RegistryEditor usergroup id (dbo.usergroup) - mirrors the backend
     * ApplicationUserPermissions.RegistryEditorUserGroupId. Adding a word to the dictionary is the
     * same "may edit the controlled vocabulary" right as adding a new fake-šifrant / registry value.
     */
    private static readonly RegistryEditorGroupId = 10;

    constructor(
        private el: ElementRef,
        private renderer: Renderer2,
        private rest: RestProviderService,
        private http: HttpClient,
        private i18n: TranslationsProviderService,
        private userService: UserService) { }

    /** Localized UI string; falls back to the given Slovene default if the key isn't in the payload. */
    private t(key: string, fallback: string): string {
        const v = this.i18n.translate(key);
        // translate() returns the raw key when it's missing from the translations payload
        return (v && v !== key) ? v : fallback;
    }

    ngAfterViewInit(): void {
        const on = this.enabled === true || this.enabled === 'true';
        if (!on) { return; }
        const node = this.el.nativeElement;
        if (!node || (node.tagName !== 'INPUT' && node.tagName !== 'TEXTAREA')) { return; }
        this.host = node;
        this.isTextarea = node.tagName === 'TEXTAREA';
        // let Angular finish first paint / CSS apply, then build the overlay
        setTimeout(() => { try { this.setup(); } catch (e) { console.log('semSpellcheck setup failed', e); } }, 0);
    }

    // ---------- setup ----------

    private setup(): void {
        if (this.setupDone || !this.host || !this.host.parentNode) { return; }
        this.setupDone = true;
        this.injectStyleOnce();

        // container (position:relative) wrapping the field
        this.container = this.renderer.createElement('div');
        this.renderer.addClass(this.container, 'sc-container');
        this.renderer.insertBefore(this.host.parentNode, this.container, this.host);

        // backdrop behind the field
        this.backdrop = this.renderer.createElement('div');
        this.renderer.addClass(this.backdrop, 'sc-backdrop');
        this.highlights = this.renderer.createElement('div');
        this.renderer.addClass(this.highlights, 'sc-highlights');
        this.renderer.appendChild(this.backdrop, this.highlights);
        this.renderer.appendChild(this.container, this.backdrop);

        // move the field into the container, on top
        this.renderer.appendChild(this.container, this.host);
        this.renderer.addClass(this.host, 'sc-host');

        // F2: kill the browser's native spell-checker on any field we decorate, so a user
        // with a Slovene OS dictionary doesn't see a competing/double underline. Only ours shows.
        this.renderer.setAttribute(this.host, 'spellcheck', 'false');
        this.renderer.setAttribute(this.host, 'autocorrect', 'off');
        this.renderer.setAttribute(this.host, 'autocapitalize', 'off');

        this.copyStyles();

        // STACKING: the overlay must paint ON TOP of the field so the wavy underline shows over the
        // field's own (often opaque) background. The static .sc-backdrop{z-index:5} is not enough when
        // the theme gives the field a higher z-index — e.g. this theme's <textarea> computes z-index:99
        // with an opaque white bg, which buries a z-index:5 backdrop and hides the squiggle entirely
        // (the visible bug on textareas; single-line inputs have a lower z-index so :5 happened to show).
        // Read the field's ACTUAL computed z-index and sit exactly one above it — robust to any theme.
        // pointer-events stays none, so painting above the field never blocks typing/clicking.
        this.alignStack();

        // listeners
        this.teardown.push(this.renderer.listen(this.host, 'input', () => { this.renderHighlights(); this.scheduleCheck(600); }));
        this.teardown.push(this.renderer.listen(this.host, 'blur', () => this.scheduleCheck(0)));
        this.teardown.push(this.renderer.listen(this.host, 'scroll', () => this.syncScroll()));
        this.teardown.push(this.renderer.listen(this.host, 'click', () => this.onHostCaret()));
        this.teardown.push(this.renderer.listen(this.host, 'keyup', () => this.syncScroll()));

        // initial check of any prefilled value
        this.renderHighlights();
        this.scheduleCheck(300);
    }

    private injectStyleOnce(): void {
        if (SpellcheckDirective.styleInjected) { return; }
        SpellcheckDirective.styleInjected = true;
        const css = `
.sc-container{position:relative;display:block;}
/* overlay paints ON TOP of the field (pointer-events:none) so the wavy underline shows over the
   input's own (often opaque) background — no dependency on making the field transparent.
   Positioned/sized in JS (alignBox) to the field's EXACT offset box, so it tracks any margin the
   theme applies to the field (a textarea's top margin otherwise shifts every line's underline). */
.sc-backdrop{position:absolute;top:0;left:0;overflow:hidden;pointer-events:none;z-index:5;}
.sc-highlights{width:100%;height:100%;box-sizing:border-box;white-space:pre;color:transparent;background:transparent;}
.sc-highlights.sc-wrap{white-space:pre-wrap;word-wrap:break-word;overflow-wrap:break-word;}
.sc-highlights .sc-mark{color:transparent;text-decoration:underline;text-decoration-style:wavy;-webkit-text-decoration-style:wavy;text-decoration-color:#e01b1b;-webkit-text-decoration-color:#e01b1b;text-decoration-skip-ink:none;text-underline-offset:1px;}
.sc-host{position:relative;z-index:1;}
.sc-popover{position:absolute;z-index:9999;background:#fff;border:1px solid #cfd6dd;border-radius:6px;box-shadow:0 4px 16px rgba(0,0,0,.18);padding:4px;min-width:150px;max-width:260px;font-size:13px;font-family:inherit;}
.sc-popover .sc-title{font-size:11px;color:#8a939b;padding:2px 8px 4px;text-transform:uppercase;letter-spacing:.03em;}
.sc-popover .sc-sugg{display:block;width:100%;text-align:left;border:0;background:transparent;padding:5px 8px;cursor:pointer;border-radius:4px;color:#1f2933;}
.sc-popover .sc-sugg:hover{background:#eef2f6;}
.sc-popover .sc-none{padding:5px 8px;color:#8a939b;font-style:italic;}
.sc-popover .sc-add{display:block;width:100%;text-align:left;border:0;border-top:1px solid #eef2f6;margin-top:4px;background:transparent;padding:6px 8px;cursor:pointer;color:#2a72c4;border-radius:0 0 4px 4px;}
.sc-popover .sc-add:hover{background:#eef6ff;}
.sc-popover .sc-msg{padding:5px 8px;color:#b23;font-size:12px;}
`;
        const style = this.renderer.createElement('style');
        this.renderer.setProperty(style, 'textContent', css);
        this.renderer.appendChild(document.head, style);
    }

    private copyStyles(): void {
        const cs = window.getComputedStyle(this.host);
        const copy = [
            'fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'fontVariant', 'letterSpacing',
            'lineHeight', 'textAlign', 'textIndent', 'textTransform', 'wordSpacing',
            'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
            'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth'
        ];
        for (const p of copy) {
            this.renderer.setStyle(this.highlights, p, (cs as any)[p]);
        }
        // line-height "normal" is font-derived and identical between the mirror <div> and the field,
        // so no override is needed — copying it verbatim keeps lines matched.
        // keep the box identical but invisible so text lands in the same place
        this.renderer.setStyle(this.highlights, 'borderStyle', 'solid');
        this.renderer.setStyle(this.highlights, 'borderColor', 'transparent');
        this.renderer.setStyle(this.highlights, 'boxSizing', 'border-box');
        if (this.isTextarea) {
            this.renderer.addClass(this.highlights, 'sc-wrap');
            // a textarea wraps: pin the mirror to the exact rendered box (border-box) so line
            // breaks fall in the same place as the real text. Re-synced on every render (growth).
            this.syncSize();
        }
        // measurement canvas uses the same font
        try {
            const canvas = document.createElement('canvas');
            this.measureCtx = canvas.getContext('2d');
            if (this.measureCtx) {
                this.measureCtx.font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
            }
        } catch (e) { this.measureCtx = null; }
    }

    // ---------- rendering ----------

    private escape(s: string): string {
        return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    private renderHighlights(): void {
        if (!this.highlights) { return; }
        this.syncSize();
        const text = this.host.value || '';
        const sorted = [...this.misspellings].sort((a, b) => a.offset - b.offset);
        let html = '';
        let idx = 0;
        for (const m of sorted) {
            if (m.offset < idx || m.offset + m.length > text.length) { continue; }
            html += this.escape(text.slice(idx, m.offset));
            html += `<span class="sc-mark">${this.escape(text.slice(m.offset, m.offset + m.length))}</span>`;
            idx = m.offset + m.length;
        }
        html += this.escape(text.slice(idx));
        html += '​'; // keep trailing line height stable
        this.renderer.setProperty(this.highlights, 'innerHTML', html);
        this.syncScroll();
    }

    private syncSize(): void {
        // pin the backdrop to the field's EXACT offset box (position + size) within the container,
        // so it tracks any margin the theme puts on the field. Otherwise the mirror sits at the
        // container's top-left while the field is offset by its margin, shifting every line's
        // underline off the word (a textarea's top margin was the visible-squiggle bug on textareas).
        if (!this.backdrop || !this.host) { return; }
        this.renderer.setStyle(this.backdrop, 'top', this.host.offsetTop + 'px');
        this.renderer.setStyle(this.backdrop, 'left', this.host.offsetLeft + 'px');
        this.renderer.setStyle(this.backdrop, 'width', this.host.offsetWidth + 'px');
        this.renderer.setStyle(this.backdrop, 'height', this.host.offsetHeight + 'px');
    }

    private alignStack(): void {
        if (!this.backdrop || !this.host) { return; }
        const hz = parseInt(window.getComputedStyle(this.host).zIndex, 10);
        const base = Number.isNaN(hz) ? 0 : hz;
        this.renderer.setStyle(this.backdrop, 'z-index', String(base + 1));
    }

    private syncScroll(): void {
        if (!this.highlights || !this.host) { return; }
        this.renderer.setStyle(this.highlights, 'transform',
            `translate(${-this.host.scrollLeft}px, ${-this.host.scrollTop}px)`);
    }

    // ---------- checking ----------

    private scheduleCheck(delay: number): void {
        if (this.debounceTimer) { clearTimeout(this.debounceTimer); }
        this.debounceTimer = setTimeout(() => this.runCheck(), delay);
    }

    private runCheck(): void {
        const text = this.host.value || '';
        if (!text.trim()) { this.misspellings = []; this.renderHighlights(); return; }
        const url = this.rest.buildWgApiUrl('api/language/spellcheck');
        const headers = { 'Authorization': 'Bearer ' + this.rest.getToken(), 'Content-Type': 'application/json' };
        const body = { Text: text, Language: this.scLanguage || 'sl', ClassType: this.scClassType || '', Field: this.scField || '' };
        this.http.post(url, body, { headers }).subscribe({
            next: (res: any) => {
                // backend serializes PascalCase (Newtonsoft default); tolerate both casings
                const list = (res && (res.Misspellings || res.misspellings)) || [];
                this.misspellings = list.map((x: any) => ({
                    word: x.Word ?? x.word,
                    offset: x.Offset ?? x.offset,
                    length: x.Length ?? x.length,
                    suggestions: x.Suggestions ?? x.suggestions ?? []
                }));
                this.renderHighlights();
            },
            error: () => { /* advisory — swallow */ }
        });
    }

    // ---------- popover ----------

    private onHostCaret(): void {
        const pos = (this.host.selectionStart == null) ? 0 : this.host.selectionStart;
        const m = this.misspellings.find(x => pos >= x.offset && pos <= x.offset + x.length);
        if (m) { this.showPopover(m); } else { this.hidePopover(); }
    }

    private showPopover(m: { word: string, offset: number, length: number, suggestions: string[] }): void {
        this.hidePopover();
        const pop = this.renderer.createElement('div');
        this.renderer.addClass(pop, 'sc-popover');

        const title = this.renderer.createElement('div');
        this.renderer.addClass(title, 'sc-title');
        this.renderer.setProperty(title, 'textContent', m.word);
        this.renderer.appendChild(pop, title);

        if (m.suggestions && m.suggestions.length) {
            for (const s of m.suggestions.slice(0, 7)) {
                const btn = this.renderer.createElement('button');
                this.renderer.setAttribute(btn, 'type', 'button');
                this.renderer.addClass(btn, 'sc-sugg');
                this.renderer.setProperty(btn, 'textContent', s);
                this.renderer.listen(btn, 'click', (ev: Event) => { ev.preventDefault(); this.applySuggestion(m, s); });
                this.renderer.appendChild(pop, btn);
            }
        } else {
            const none = this.renderer.createElement('div');
            this.renderer.addClass(none, 'sc-none');
            this.renderer.setProperty(none, 'textContent', this.t('galis_js_spellcheck_no_suggestions', 'Ni predlogov'));
            this.renderer.appendChild(pop, none);
        }

        if (this.canAddToDictionary()) {
            const add = this.renderer.createElement('button');
            this.renderer.setAttribute(add, 'type', 'button');
            this.renderer.addClass(add, 'sc-add');
            // "+ " prefix stays a UI decoration in the template; the translated value is just the label
            this.renderer.setProperty(add, 'textContent', '+ ' + this.t('galis_js_spellcheck_add_to_dictionary', 'Dodaj v slovar'));
            this.renderer.listen(add, 'click', (ev: Event) => { ev.preventDefault(); this.addToDictionary(m, pop); });
            this.renderer.appendChild(pop, add);
        }

        this.renderer.appendChild(this.container, pop);
        this.popover = pop;
        this.positionPopover(pop, m);

        // dismiss on outside click
        const off = this.renderer.listen('document', 'mousedown', (ev: any) => {
            if (this.popover && !this.popover.contains(ev.target) && ev.target !== this.host) { this.hidePopover(); }
        });
        (pop as any).__off = off;
    }

    private positionPopover(pop: HTMLElement, m: { offset: number }): void {
        let x = 0;
        const cs = window.getComputedStyle(this.host);
        const padL = parseFloat(cs.paddingLeft) || 0;
        if (!this.isTextarea && this.measureCtx) {
            const prefix = (this.host.value || '').slice(0, m.offset);
            x = padL + this.measureCtx.measureText(prefix).width - this.host.scrollLeft;
        } else {
            x = padL;
        }
        const maxX = Math.max(0, this.host.offsetWidth - 160);
        x = Math.min(Math.max(0, x), maxX);
        this.renderer.setStyle(pop, 'left', x + 'px');
        this.renderer.setStyle(pop, 'top', (this.host.offsetTop + this.host.offsetHeight + 2) + 'px');
    }

    private applySuggestion(m: { offset: number, length: number }, s: string): void {
        const text = this.host.value || '';
        const next = text.slice(0, m.offset) + s + text.slice(m.offset + m.length);
        this.host.value = next;
        this.host.dispatchEvent(new Event('input', { bubbles: true }));
        this.hidePopover();
        this.scheduleCheck(0);
    }

    /**
     * Mirror of the backend ApplicationUserPermissions.MayEditRegistry() gate, using the same
     * post-login user info the rest of the app reads (UserService, from the "user" localStorage
     * payload): Administrators or RegistryEditor (group 10) members. The button is hidden for anyone
     * else - fail closed. The endpoint's 403 stays as server-side defense-in-depth.
     */
    private canAddToDictionary(): boolean {
        const u: any = this.userService.getLoggedUser();
        const admin = !!(u && u.IsAdmin);
        return admin || this.userService.checkIfInUserGroup(SpellcheckDirective.RegistryEditorGroupId);
    }

    private addToDictionary(m: { word: string }, pop: HTMLElement): void {
        const url = this.rest.buildWgApiUrl('api/language/spellcheck/dictionary');
        const headers = { 'Authorization': 'Bearer ' + this.rest.getToken(), 'Content-Type': 'application/json' };
        this.http.post(url, { Word: m.word, Language: this.scLanguage || 'sl' }, { headers }).subscribe({
            next: () => { this.hidePopover(); this.scheduleCheck(0); },
            error: (e: any) => {
                const msg = this.renderer.createElement('div');
                this.renderer.addClass(msg, 'sc-msg');
                this.renderer.setProperty(msg, 'textContent',
                    (e && e.status === 403)
                        ? this.t('galis_js_spellcheck_no_permission', 'Ni dovoljenja za dodajanje')
                        : this.t('galis_js_spellcheck_add_error', 'Napaka pri dodajanju'));
                this.renderer.appendChild(pop, msg);
            }
        });
    }

    private hidePopover(): void {
        if (this.popover) {
            const off = (this.popover as any).__off;
            if (off) { try { off(); } catch (e) { } }
            if (this.popover.parentNode) { this.renderer.removeChild(this.popover.parentNode, this.popover); }
            this.popover = null;
        }
    }

    ngOnDestroy(): void {
        if (this.debounceTimer) { clearTimeout(this.debounceTimer); }
        this.hidePopover();
        for (const t of this.teardown) { try { t(); } catch (e) { } }
        this.teardown = [];
    }
}
